/**
 * Copyright (c) 2026 Farelanes LLC. All rights reserved.
 *
 * Client calls for profile, Auth0 verify, subscription, session claim/heartbeat,
 * admin config, and RFQ upload. Singleton instance is the default export.
 */

import {API_BASE_URL} from "../common/ApiUrl";
import axios from "axios";
import {logger} from "../utils/logger";


class UserService {

    upload(file, serverToken) {
        let formData = new FormData();
        formData.append("file", file);
        return axios.post(API_BASE_URL + 'upload', {
            file
        }, {headers: {
            Authorization: `Bearer ${serverToken}`,
            "Content-Type": "multipart/form-data",
        }})
    }

    getFactorProfiles(token) {
        return axios.get(`${API_BASE_URL}factor-profiles`, { headers: { Authorization: `Bearer ${token}` } });
    }

    doGeneralDataBigSearch(startday, endday, source, token) {
        const startParam = startday != null && startday !== '' ? startday : '';
        const endParam = endday != null && endday !== '' ? endday : '';
        return axios.get(`${API_BASE_URL}search/general-lane-data?start_date=${encodeURIComponent(startParam)}&end_date=${encodeURIComponent(endParam)}&source=${encodeURIComponent(source || '')}`, {headers: {Authorization: `Bearer ${token}`}});
    }

    citySearch(id, token) {
        return axios.get(`${API_BASE_URL}cities?id=${id}`, {headers: {Authorization: `Bearer ${token}`}});
    }

    cityWeatherSearch(location, token) {
        return axios.get(`${API_BASE_URL}dashboard-weather?location=${location}&days=5`, {headers: {Authorization: `Bearer ${token}`}});

    }

    cityFuelSearch(location, token) {
        return axios.get(`${API_BASE_URL}dashboard-fuel?id=${location}`, {headers: {Authorization: `Bearer ${token}`}});
    }

    cityFuelSearchByZip(zip, token) {
        return axios.get(`${API_BASE_URL}dashboard-fuel-zip?zip=${zip}`, {headers: {Authorization: `Bearer ${token}`}});
    }

    cityFuelSearchByCityState(city, state, token) {
        return axios.get(`${API_BASE_URL}dashboard-fuel-zip?city=${city}&state=${state}`, {headers: {Authorization: `Bearer ${token}`}});
    }




    laneAnalytics(startdate, endday, token) {
        return axios.get(`${API_BASE_URL}search/lane-analytics?end_date=${endday}&start_date=${startdate}`, {headers: {Authorization: `Bearer ${token}`}});
    }

    getUserSubscription(token, user, forceRefresh = false) {
        const url = forceRefresh 
            ? API_BASE_URL + `payments/subscriptions?forceRefresh=true`
            : API_BASE_URL + `payments/subscriptions`;
        return axios.get(url, {headers: {Authorization: `Bearer ${token}`}}).then(res => res.data);
    }

    getTicker(token, user, ticker) {
        let tickerUrl = '';
        if (ticker === 1) {
            tickerUrl = 'one'
        } else {
            tickerUrl = 'two'
        }
        return axios.get(API_BASE_URL + 'ticker/' + tickerUrl , {headers: {Authorization: `Bearer ${token}`}});
    }

    getWeatherAndFuelTicker(pickCityId,
                            pickCity,
                            pickState,
                            dropCityId,
                            dropCity,
                            dropState,
                            token) {
        return axios.post(`${API_BASE_URL}ticker/weather-and-fuel`, {
            pickCityId: pickCityId,
            pickCity: pickCity,
            pickState: pickState,
            dropCityId: dropCityId,
            dropCity: dropCity,
            dropState: dropState
        }, {headers: {Authorization: `Bearer ${token}`}})
    }

    me(token, user) {
        return axios.get(API_BASE_URL + 'user' , {headers: {Authorization: `Bearer ${token}`}});
    }

    config(token, user) {
        return axios.get(API_BASE_URL + 'config' , {headers: {Authorization: `Bearer ${token}`}});
    }

    userSettingsFlow(token, user) {
        return axios.get(API_BASE_URL + 'user' + '/settings-flow' , {headers: {Authorization: `Bearer ${token}`}});
    }

    userSubscriptionStatus(token, user) {
        return axios.get(API_BASE_URL + 'user' + '/subscription-status' , {headers: {Authorization: `Bearer ${token}`}});
    }

    /**
     * Register this browser as the active device session. Displaces older sessions beyond the
     * tier's device limit. Never cached, so an eviction can always be followed by a fresh claim.
     */
    claimSession(token) {
        logger.debug('POST session/claim');
        return axios.post(API_BASE_URL + 'session/claim', {}, {
            headers: {Authorization: `Bearer ${token}`, 'X-Skip-Cache': 'true'}
        });
    }

    sessionHeartbeat(token) {
        return axios.post(API_BASE_URL + 'session/heartbeat', {}, {
            headers: {Authorization: `Bearer ${token}`, 'X-Skip-Cache': 'true'}
        });
    }

    /** Token is injected by the axios interceptor; callers on the logout path have none in hand. */
    releaseSession(sessionId) {
        return axios.delete(API_BASE_URL + 'session', {
            headers: {'X-Session-Id': sessionId, 'X-Skip-Cache': 'true'}
        });
    }

    userSubscriptionStatusPair(token, user) {
        return axios.get(API_BASE_URL + 'user' + '/subscription-status-pair' , {headers: {Authorization: `Bearer ${token}`}});
    }

    resendVerificationEmail(token) {
        return axios.post(API_BASE_URL + 'user' + '/resend-verification-email', {}, {headers: {Authorization: `Bearer ${token}`}});
    }

    mySubscription(token, user) {
        return axios.get(API_BASE_URL + 'users/me/subscription', {headers: {Authorization: `Bearer ${token}`}});
    }

    verify(user, token) {
        return axios.post(API_BASE_URL + 'verify', {
            sub: user.sub,
            email: user.email,
            given_name: user.given_name,
            locale: user.locale,
            picture: user.picture,
            name: user.name,
            nickname: user.nickname,
            family_name: user.family_name
        }, {headers: {Authorization: `Bearer ${token}`}});
    }

    getLaneData(token, user) {
        return axios.get(API_BASE_URL + 'dashboard/lane-statistics', {headers: {Authorization: `Bearer ${token}`}});
    }

    user(token) {
        return axios
            .get(API_BASE_URL + `user`, { headers: { Authorization: `Bearer ${token}` }})
            .then(res => res.data);
    }

    putUserMCALEI(payload, token) {
        return axios.put(API_BASE_URL + 'users/me/carrier-info', {
            sub: payload.sub,
            motorCarrierNumber: payload.motorCarrierNumber,
            dotNumber: payload.dotNumber,
            legalCompanyName: payload.legalCompanyName,
            formationDate: payload.formationDate,
            companyAddress: payload.companyAddress,
            companyAddressLineTwo: payload.companyAddressLineTwo,
            companyCity: payload.city,
            companyState: payload.state,
            companyZip: payload.zip,
            companyPhone: payload.companyPhone,
            companyEmail: payload.companyEmail,
            companyNli: payload.nlt,
            ibrnNumber: payload.ibrn,
            lei: payload.lei,
            dnbNumber: payload.dnbNumber,
            smartPrefixField: payload.smartPrefixField,
            subPrefixField: payload.subPrefixField,
        }, {headers: {Authorization: `Bearer ${token}`}});
    }

    putUserTickersAndPairs(payload, token) {
        return axios.put(API_BASE_URL + 'users/me/lane-pairs', {
            enable_assesorials: payload.enable_assesorials,
            sub: payload.sub,
            pairs: payload.pairs,
            tickerOne: payload.tickerOne,
            tickerTwo: payload.tickerTwo,
            tickerOneName: payload.tickerOneName,
            tickerTwoName: payload.tickerTwoName
        }, {headers: {Authorization: `Bearer ${token}`}});
    }

    updateConfig(payload, token) {
        return axios.put(API_BASE_URL + 'config/update', payload, {headers: {Authorization: `Bearer ${token}`}});
    }

    doProductionDataIngestion(days, miles, lessThanLineHaulAmount, greaterThanLineHaulAmount, token) {
        return axios.get(API_BASE_URL + `index/production_lane_data?number_of_days_from_today=${days}&total_miles=${miles}&line_haul_amount_greater_than=${greaterThanLineHaulAmount}&line_haul_amount_less_than=${lessThanLineHaulAmount}`, {headers: {Authorization: `Bearer ${token}`}});
    }

    putUser(payload, token) {
        return axios.put(API_BASE_URL + 'users/me', {
            firstName: payload.firstName,
            lastName: payload.lastName,
            mobileNumber: payload.mobileNumber,
            addressLineOne: payload.addressLineOne,
            addressLineTwo: payload.addressLineTwo,
            state: payload.state,
            city: payload.city,
            country: payload.country,
            zipCode: payload.zipCode,
            sub: payload.sub,
            howDoYouKnowUs: payload.howDoYouKnowUs,
            userTypes: payload.userTypes,
        }, {headers: {Authorization: `Bearer ${token}`}});
    }
}

export default new UserService();